import { motion } from "framer-motion";
import { Sparkles, Gift, HeartHandshake, Truck } from "lucide-react";
import MaskReveal from "./MaskReveal";

const EASE = [0.16, 1, 0.3, 1];

const HIGHLIGHTS = [
  { icon: Sparkles, text: "Produtos artesanais & selecionados um a um" },
  { icon: Gift, text: "Embalagens preparadas com mimos e muito carinho" },
  { icon: HeartHandshake, text: "Atendimento personalizado via WhatsApp" },
  { icon: Truck, text: "Entrega para todo o Brasil via Shopee" },
];

export default function About() {
  return (
    <section id="sobre" className="relative overflow-hidden py-20 sm:py-28" data-testid="about-section">
      <div className="absolute -right-40 top-10 -z-10 h-96 w-96 rounded-full bg-pinklight/70 blur-[120px]" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -50, rotate: -4 }}
          whileInView={{ opacity: 1, x: 0, rotate: -2 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative mx-auto w-fit"
        >
          <img
            src="images/produtos/sobre.jpg"
            alt="Produtos Anime Glam com letreiro neon da marca"
            loading="lazy"
            decoding="async"
            data-testid="about-photo"
            className="w-full max-w-lg rounded-[2.5rem] border-8 border-white object-cover shadow-[0_30px_70px_rgba(183,148,230,0.35)]"
          />
          <motion.span
            initial={{ scale: 0, rotate: 20 }}
            whileInView={{ scale: 1, rotate: 8 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.4 }}
            className="absolute -right-3 -top-4 rounded-2xl bg-goldy px-5 py-3 font-display text-sm font-bold text-ink shadow-[0_12px_28px_rgba(229,184,66,0.5)] sm:-right-8"
          >
            ✦ 100% feito com amor
          </motion.span>
        </motion.div>

        <div className="text-center lg:text-left">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-4 inline-block rounded-full border-2 border-lilaclight bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-lilac"
          >
            Nossa história
          </motion.span>

          <MaskReveal delay={0.1}>
            <h2 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Feito à mão, com alma de anime
            </h2>
          </MaskReveal>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
            className="mt-6 text-base font-medium leading-relaxed text-inksoft sm:text-lg"
          >
            A Anime Glam nasceu da paixão pela cultura pop japonesa, pelos animes e
            pelo universo kawaii. Cada acessório, agenda e item decorativo é escolhido
            e produzido com carinho especial por Camila Machado para trazer mais cor,
            encanto e brilho ao seu dia a dia.
          </motion.p>

          <div className="mt-9 grid gap-4 sm:grid-cols-2">
            {HIGHLIGHTS.map(({ icon: Icon, text }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.15 + i * 0.1 }}
                className="flex items-center gap-3 rounded-2xl border-2 border-pink-100 bg-white/80 p-4 text-left backdrop-blur transition-colors duration-300 hover:border-pinky"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-tr from-pinklight to-lilaclight text-lilac">
                  <Icon size={18} />
                </span>
                <span className="text-sm font-bold leading-snug text-ink">{text}</span>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-8 font-hand text-4xl text-lilac"
          >
            Camila Machado
            <span className="ml-3 text-xl text-inksoft/70">fundadora & criadora</span>
          </motion.p>
        </div>
      </div>
    </section>
  );
}
